import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MapEngine from "./map/MapEngine";
import { COMUNI } from "./map/comuni";
import { businessMarkerEl, comuneMarkerEl, eventMarkerEl, riderMarkerEl } from "./map/markers";

function nearestComune(lat, lng) {
  let best = null, bd = Infinity;
  COMUNI.forEach((c) => {
    const d = (c.center[1] - lat) ** 2 + (c.center[0] - lng) ** 2;
    if (d < bd) { bd = d; best = c; }
  });
  return best;
}

// Mappa immersiva del territorio riutilizzabile (CEO / Centrale / Rider).
// Stessa scenografia neon, comuni, attività, eventi, rider live e movimento simulato.
export default function TerritoryMap({ height = 480 }) {
  const { user } = useAuth();
  const isAdmin = ["ceo", "ops", "admin"].includes(user?.role);
  const [pois, setPois] = useState([]);
  const [events, setEvents] = useState([]);
  const [riders, setRiders] = useState([]);
  const [ghosts, setGhosts] = useState(0);
  const [ghostRoutes, setGhostRoutes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    const lm = () => api.get("/public/map").then(({ data }) => setPois(data.pois || [])).catch(() => {});
    const le = () => api.get("/public/events").then(({ data }) => setEvents(Array.isArray(data) ? data : [])).catch(() => {});
    const ll = () => api.get("/public/live").then(({ data }) => { setRiders(data.riders || []); setGhosts(data.ghosts || 0); }).catch(() => {});
    lm(); le(); ll();
    const t1 = setInterval(() => { if (!document.hidden) { lm(); le(); } }, 45000);
    const t2 = setInterval(() => { if (!document.hidden) ll(); }, 12000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  useEffect(() => {
    if (!ghosts) { setGhostRoutes([]); return; }
    let stop = false;
    const load = () => api.get(`/public/ghost-routes?n=${ghosts}`).then(({ data }) => { if (!stop) setGhostRoutes(data.routes || []); }).catch(() => {});
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 600000);
    return () => { stop = true; clearInterval(t); };
  }, [ghosts]);

  const comuni = useMemo(() => {
    const cnt = {}, off = {};
    (pois || []).forEach((p) => {
      if (p.lat == null || p.lng == null) return;
      const c = nearestComune(p.lat, p.lng);
      if (!c) return;
      cnt[c.id] = (cnt[c.id] || 0) + 1;
      if (p.has_offer) off[c.id] = true;
    });
    return COMUNI.map((c) => ({ ...c, lat: c.center[1], lng: c.center[0], count: cnt[c.id] || 0, hasOffer: !!off[c.id] }));
  }, [pois]);

  const layers = useMemo(() => {
    const l = [];
    l.push({ id: "comuni", anchor: "bottom", sig: (c) => `${c.id}${c.count}${c.hasOffer}`, items: comuni, build: (c) => comuneMarkerEl(c, (cc) => setFocus({ lat: cc.lat, lng: cc.lng, zoom: 14.2 }), false, 0) });
    l.push({ id: "poi", sig: (p) => `${p.id}${p.lat}${p.lng}${p.has_offer}${selected && selected.id === p.id}`, items: (pois || []).filter((p) => p.lat != null).map((p, i) => ({ ...p, _i: i })), build: (p) => businessMarkerEl(p, setSelected, p._i || 0, !!(selected && selected.id === p.id)) });
    if (events.length) l.push({ id: "events", anchor: "bottom", noCull: true, sig: (e) => `${e.id}${e.is_live}${e.sponsored}`, items: events.filter((e) => e.lat != null), build: (e) => eventMarkerEl(e, () => {}) });
    if (riders.length) l.push({ id: "riders", anchor: "center", noCull: true, sig: (r) => r.id, items: riders, build: (r) => riderMarkerEl(r.id) });
    return l;
  }, [comuni, pois, events, riders, selected]);

  const ghostSeeds = useMemo(() => {
    const s = (pois || []).filter((p) => p.lat != null).map((p) => [p.lng, p.lat]);
    COMUNI.filter((c) => c.active).forEach((c) => s.push(c.center));
    return s;
  }, [pois]);

  return (
    <div style={{ position: "relative", height, borderRadius: 20, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="territory-map">
      <MapEngine layers={layers} center={[7.628, 45.216]} zoom={11.2} pitch={42} focus={focus} ghosts={ghosts} ghostSeeds={ghostSeeds} ghostRoutes={ghostRoutes} />
      <div className="absolute left-3 top-3 z-30 flex flex-wrap gap-2 pointer-events-none">
        <span className="rounded-full bg-[#0A0A0C]/80 border border-white/10 px-3 py-1 text-xs backdrop-blur-md">{(pois || []).length} attività</span>
        {events.length > 0 && <span className="rounded-full bg-[#0A0A0C]/80 border border-[#A78BFA]/30 px-3 py-1 text-xs text-[#C4B5FD] backdrop-blur-md">{events.length} eventi</span>}
        {riders.length > 0 && <span className="rounded-full bg-[#0A0A0C]/80 border border-cyan-400/30 px-3 py-1 text-xs text-cyan-300 backdrop-blur-md">{riders.length} rider live</span>}
        {isAdmin && ghosts > 0 && <span className="rounded-full bg-[#0A0A0C]/80 border border-amber-400/30 px-3 py-1 text-xs text-amber-300 backdrop-blur-md" title="Movimento simulato (solo admin)">Simulazione</span>}
      </div>
      {selected && (
        <div className="absolute left-3 right-3 bottom-3 z-30 bg-[#0A0A0C]/95 border border-white/15 rounded-2xl p-3 backdrop-blur-xl" data-testid="tm-selected">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold truncate">{selected.name}</div>
              <div className="text-xs text-zinc-400 truncate">{selected.zone}{selected.rating > 0 ? ` · ⭐ ${selected.rating}` : ""}{selected.has_offer ? " · ✦ Offerta" : ""}</div>
            </div>
            <button data-testid="tm-selected-close" onClick={() => setSelected(null)} className="text-zinc-400 hover:text-white text-lg leading-none">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
